import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Home } from "../pages/home";
import { Ocurrences } from "../pages/ocurrences";
import { Report } from "../pages/report";
import { Alerts } from "../pages/alerts";
import { Profile } from "../pages/profile";
import { PersonalData } from "../pages/settings/personal-data";
import { TermsOfUse } from "../pages/settings/terms-of-use";
import { PrivacyPolicy } from "../pages/settings/privacy-policy";
import { HelpSupport } from "../pages/settings/help-support";
import AppBar from "../components/AppBar";
import TabNavigator from "../components/TabNavigator";
import { ChangeProfile } from "@/pages/profile/change-profile";
import { OccurrenceDetails } from "@/pages/ocurrences/details";
import { WorkOrderDetails } from "@/pages/work-orders/details";
import { Login } from "@/pages/auth/login";
import { Register } from "@/pages/auth/register";
import { RecoveryPassword } from "@/pages/auth/recovery-password";
import { VerifyEmail } from "@/pages/auth/verify-email";
import { ResetPassword } from "@/pages/auth/reset-password";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useSSENotifications } from "@/hooks/useSSENotifications";
import { useSentryUser } from "@/hooks/useSentryUser";

const AppLayout = () => {
  const location = useLocation();
  const [sseInitialized, setSseInitialized] = useState(false);

  // Mantém o usuário anexado ao Sentry em sincronia com a sessão atual
  // (login, cadastro, restauração de sessão no boot e logout).
  useSentryUser();

  // Conectar ao SSE apenas se usuário estiver autenticado e não estiver em rota pública
  const publicRoutes = [
    "/",
    "/login",
    "/register",
    "/recovery-password",
    "/verify-email",
    "/reset-password",
  ];
  const isPublicRoute = publicRoutes.includes(location.pathname);
  
  // Ativar SSE notificações para usuários autenticados (verifica via token no localStorage)
  const hasToken = !!localStorage.getItem("duas-vidas-token");
  const shouldConnect = !isPublicRoute && hasToken;
  
  // Inicializar SSE apenas uma vez
  useEffect(() => {
    if (shouldConnect && !sseInitialized) {
      setSseInitialized(true);
    }
  }, [shouldConnect]);
  
  useSSENotifications(sseInitialized);

  // Rotas sem TabNavigator
  const routesWithoutTabNavigator = ["/report"];
  const showTabNavigator = !routesWithoutTabNavigator.includes(
    location.pathname
  );

  return (
    <>
      {!isPublicRoute && <AppBar />}
      <Routes>
        {/* Rotas públicas (autenticação) */}
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/recovery-password" element={<RecoveryPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Rotas autenticadas */}
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ocurrences"
          element={
            <ProtectedRoute>
              <Ocurrences />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ocurrences/details/:id"
          element={
            <ProtectedRoute>
              <OccurrenceDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/work-orders/:id"
          element={
            <ProtectedRoute>
              <WorkOrderDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/report"
          element={
            <ProtectedRoute>
              <Report />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alerts"
          element={
            <ProtectedRoute>
              <Alerts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/change-profile"
          element={
            <ProtectedRoute>
              <ChangeProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/personal-data"
          element={
            <ProtectedRoute>
              <PersonalData />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/terms-and-privacy"
          element={
            <ProtectedRoute>
              <TermsOfUse />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/terms-of-use"
          element={
            <ProtectedRoute>
              <TermsOfUse />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/privacy-policy"
          element={
            <ProtectedRoute>
              <TermsOfUse />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/help-support"
          element={
            <ProtectedRoute>
              <HelpSupport />
            </ProtectedRoute>
          }
        />
      </Routes>
      {!isPublicRoute && showTabNavigator && <TabNavigator />}
    </>
  );
};

export const Router = () => {
  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  );
};
